import React, { useState } from "react"
import {
	FaExclamationTriangle,
	FaExclamationCircle,
	FaCalendarCheck,
	FaTools,
	FaChevronDown,
	FaChevronUp,
	FaCheckCircle
} from "react-icons/fa"
import { isCenterClosed } from "../../../utils/generalUtils"

function getIssueCounts(futureHuddleData, centerOpen, centerClosedDates = []) {
	const counts = {}
	futureHuddleData.forEach(day => {
		const dateObj = new Date(day.date + "T12:00:00-08:00")
		if (
			isCenterClosed(dateObj, centerOpen, centerClosedDates) ||
			day.metrics?.current_scheduled_patients === 0
		)
			return
		if (Array.isArray(day.schedulingIssues)) {
			day.schedulingIssues.forEach(issue => {
				const type = issue.type || "Other"
				counts[type] = (counts[type] || 0) + 1
			})
		}
	})
	return counts
}

function getMostAndLeastAffectedDay(futureHuddleData, centerOpen, centerClosedDates = []) {
	let most = null,
		least = null
	futureHuddleData.forEach(day => {
		const dateObj = new Date(day.date + "T12:00:00-08:00")
		if (
			isCenterClosed(dateObj, centerOpen, centerClosedDates) ||
			day.metrics?.current_scheduled_patients === 0
		)
			return
		const count = Array.isArray(day.schedulingIssues) ? day.schedulingIssues.length : 0
		if (most === null || count > most.count) most = { date: day.date, count }
		if (least === null || (count < least.count && count > 0)) least = { date: day.date, count }
	})
	return { most, least }
}

// Mapping of issue types to remediation strategies
const remediationStrategies = {
	Linked: "Ensure that patients have sufficient time between clinic appointments and their infusion start.",
	Scheduling: "Ensure that patients are scheduled to start and end within clinic hours.",
	Template: "Ensure that the patients currently scheduled are aligned with the template slots.",
	Other: "Review individual cases"
}

// Function to format date nicely
function formatDate(dateStr) {
	return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, {
		weekday: "long",
		month: "short",
		day: "numeric"
	})
}

const SchedulingIssues = ({ futureHuddleData, settings }) => {
	const centerOpen = settings.centerOpen
	const centerClosedDates = settings?.center_closed_dates || []
	const [showAllIssues, setShowAllIssues] = useState(false)
	const issueCounts = getIssueCounts(futureHuddleData, centerOpen, centerClosedDates)
	const { most, least } = getMostAndLeastAffectedDay(
		futureHuddleData,
		centerOpen,
		centerClosedDates
	)
	const totalIssues = Object.values(issueCounts).reduce((a, b) => a + b, 0)

	// Sort issues by count to show most prominent first
	const sortedIssues = Object.entries(issueCounts)
		.sort((a, b) => b[1] - a[1])
		.map(([type, count]) => ({ type, count }))

	// Determine which issues to display based on toggle state
	const issuesToShow = showAllIssues ? sortedIssues : sortedIssues.slice(0, 3)

	// Get the most common issue type
	const mostCommonIssue = sortedIssues.length > 0 ? sortedIssues[0].type.split(" ")[0] : null

	return (
		<div className="bg-white rounded-lg shadow p-6 flex flex-col min-h-[220px]">
			<h2 className="text-2xl font-semibold mb-5 flex items-center">
				<div className="p-2 bg-orange-100 rounded-full mr-2">
					<FaExclamationTriangle className="text-orange-600" />
				</div>
				<span className="text-orange-600">Scheduling Issues</span>
			</h2>

			{/* Issue Summary */}
			<div className="bg-gradient-to-r from-orange-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-orange-500 mb-4">
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center">
						<div className="p-1.5 bg-orange-100 rounded-full mr-2">
							<FaExclamationCircle className="text-orange-600 text-sm" />
						</div>
						<span className="font-semibold text-gray-800">Issue Summary</span>
					</div>
					<div className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-sm font-medium">
						{totalIssues} Total Issues
					</div>
				</div>

				{/* Issue Type Breakdown */}
				<div className="mt-2 space-y-2">
					{issuesToShow.map(({ type, count }) => (
						<div key={type} className="flex items-center justify-between">
							<div className="flex items-center">
								<div className="w-1 h-4 bg-orange-400 rounded-full mr-2"></div>
								<span className="text-gray-700">
									{type
										.replace(/([A-Z])/g, " $1")
										.replace(/^./, str => str.toUpperCase())}
								</span>
							</div>
							<div className="flex items-center">
								<span className="bg-white border border-orange-200 rounded-full px-2 py-0.5 text-xs font-semibold text-orange-700">
									{count}
								</span>
								<div className="w-16 h-2 bg-gray-200 rounded-full ml-2 overflow-hidden">
									<div
										className="h-full bg-orange-400 rounded-full"
										style={{ width: `${(count / totalIssues) * 100}%` }}
									></div>
								</div>
							</div>
						</div>
					))}

					{sortedIssues.length > 3 && (
						<button
							onClick={() => setShowAllIssues(!showAllIssues)}
							className="text-xs flex items-center text-orange-600 hover:text-orange-800 focus:outline-none mt-1"
						>
							{showAllIssues ? (
								<>
									<span>Show Less</span>
									<FaChevronUp className="ml-1 text-xs" />
								</>
							) : (
								<>
									<span>Show All {sortedIssues.length} Issues</span>
									<FaChevronDown className="ml-1 text-xs" />
								</>
							)}
						</button>
					)}
				</div>

				{/* Concise Remediation Tip */}
				{mostCommonIssue && (
					<div className="mt-3 flex items-center bg-white rounded-md p-2 border border-orange-100">
						<div className="p-1.5 bg-green-100 rounded-full mr-2">
							<FaTools className="text-green-600 text-xs" />
						</div>
						<div className="text-sm">
							<span className="font-medium">Remediation: </span>
							<span className="text-gray-700">
								{remediationStrategies[mostCommonIssue] ||
									remediationStrategies["Other"]}
							</span>
						</div>
					</div>
				)}
			</div>

			{/* Day Analysis */}
			<div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-3 shadow-sm border-l-4 border-blue-500">
				<div className="flex items-center mb-2">
					<div className="p-1.5 bg-blue-100 rounded-full mr-2">
						<FaCalendarCheck className="text-blue-600 text-sm" />
					</div>
					<span className="font-semibold text-gray-800">Day Analysis</span>
				</div>

				{most && most.count > 0 && (
					<div className="flex items-center justify-between mb-2 bg-white rounded-md p-2 border border-blue-100">
						<div className="flex items-center">
							<div className="p-1 bg-red-100 rounded-full mr-2">
								<FaExclamationCircle className="text-red-500 text-xs" />
							</div>
							<span className="text-sm">Most affected</span>
						</div>
						<div className="flex items-center">
							<span className="font-medium text-sm">{formatDate(most.date)}</span>
							<span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
								{most.count} issues
							</span>
						</div>
					</div>
				)}

				{least && least.count > 0 && (
					<div className="flex items-center justify-between bg-white rounded-md p-2 border border-blue-100">
						<div className="flex items-center">
							<div className="p-1 bg-green-100 rounded-full mr-2">
								<FaCheckCircle className="text-green-500 text-xs" />
							</div>
							<span className="text-sm">Least affected</span>
						</div>
						<div className="flex items-center">
							<span className="font-medium text-sm">{formatDate(least.date)}</span>
							<span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
								{least.count} issues
							</span>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default SchedulingIssues
